import type { AriaAttributes } from 'react';

import type { RegionNode } from '../api/regions';

import { handleTreeKeys } from './region-tree-keys';
import { panelStyle, type RegionTreeVariant } from './region-tree-styles';
import RegionTreeNode from './RegionTreeNode';
import RegionTreeTrigger from './RegionTreeTrigger';
import { useRegionTreeSelect } from './use-region-tree-select';

interface RegionTreeSelectProps extends Pick<
  AriaAttributes,
  'aria-labelledby' | 'aria-describedby' | 'aria-required' | 'aria-invalid'
> {
  regions: RegionNode[];
  value: string;
  onChange: (id: string) => void;
  disabledIds?: Set<string>;
  placeholder?: string;
  className?: string;
  /** When set, render a pseudo-row at the top of the dropdown that clears
   *  the selection (`onChange('')`). Useful for filter contexts where
   *  "no region picked" means "all regions". */
  clearLabel?: string;
  /** `dashboards` styles the trigger and panel from `--nb-*` tokens. */
  variant?: RegionTreeVariant;
}

function findLabel(nodes: RegionNode[], id: string): string | null {
  for (const n of nodes) {
    if (n.id === id) return n.name;
    const found = findLabel(n.children, id);
    if (found) return found;
  }
  return null;
}

const CLEAR_NODE = { id: '', children: [] } as unknown as RegionNode;

export default function RegionTreeSelect({
  regions,
  value,
  onChange,
  disabledIds = new Set(),
  placeholder = 'Select region...',
  className = '',
  clearLabel,
  variant = 'default',
  ...aria
}: RegionTreeSelectProps) {
  const { open, setOpen, containerRef, triggerRef, select, onKeyDown } =
    useRegionTreeSelect(onChange);
  const selectedLabel = value ? findLabel(regions, value) : null;
  const displayLabel = selectedLabel ?? (value === '' && clearLabel ? clearLabel : null);
  const row = (node: RegionNode) => (
    <RegionTreeNode
      key={node.id}
      node={node}
      depth={0}
      value={value}
      disabledIds={disabledIds}
      variant={variant}
      onSelect={select}
    />
  );

  return (
    <div ref={containerRef} className={`relative ${className}`} onKeyDown={onKeyDown}>
      <RegionTreeTrigger
        ref={triggerRef}
        open={open}
        label={displayLabel}
        placeholder={placeholder}
        variant={variant}
        onToggle={() => setOpen((v) => !v)}
        {...aria}
      />
      {open && (
        <div
          role="tree"
          onKeyDown={handleTreeKeys}
          className="absolute z-50 mt-1 w-full min-w-52 max-h-64 overflow-y-auto rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-[#1e2521] shadow-lg py-1"
          style={panelStyle(variant)}
        >
          {clearLabel !== undefined && row({ ...CLEAR_NODE, name: clearLabel })}
          {regions.length === 0 ? (
            <p className="px-3 py-2 text-xs text-stone-400">No regions available</p>
          ) : (
            regions.map(row)
          )}
        </div>
      )}
    </div>
  );
}
